import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface Props {
  /** The app-level session (already resolved before the router renders), so a
   *  logged-out visitor never pays for a role lookup. */
  session: Session | null;
  children: React.ReactNode;
}

/** Shared cache key — one `users.role` read serves every gated route. */
const VIEWER_ROLE_QUERY_KEY = 'viewer-role';

/**
 * Wraps every route an `agent` must not reach. Agents are a soft register in
 * v1 (no search, no messaging, no notifications, no profile, no listings), so
 * their entire in-product surface is the /coming-soon splash plus /settings.
 * Anything else — typed in the URL bar or reached from a stale link — bounces
 * back to the splash.
 *
 * States:
 *   no session   → children (a logged-out visitor can't be an agent; this gate
 *                  also wraps public pages, which must not flash a spinner)
 *   role loading → spinner (cached for 5 min, so it costs one read per session)
 *   agent        → /coming-soon
 *   anything else→ children (tenant / owner / guarantor are untouched)
 *
 * Fails OPEN on a read error: a transient network blip must never lock a tenant
 * or an owner out of the product. The data itself stays protected by RLS —
 * this gate is a navigation restriction, not the security boundary.
 */
const AgentGate = ({ session, children }: Props) => {
  const authId = session?.user?.id ?? null;

  const { data: role, isPending } = useQuery({
    queryKey: [VIEWER_ROLE_QUERY_KEY, authId],
    enabled: !!authId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('users')
        .select('role')
        .eq('auth_id', authId as string)
        .maybeSingle();
      // Throw rather than returning null: a failed read must not be cached as a
      // confident "not an agent" for the next 5 minutes.
      if (error) throw error;
      return data?.role ?? null;
    },
  });

  if (!authId) return <>{children}</>;

  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  if (role === 'agent') return <Navigate to="/coming-soon" replace />;

  return <>{children}</>;
};

export default AgentGate;
